import { CartAddEvent, ThemeEvents, runCartMutation, isLatestCartUpdate } from '@theme/events';

let recommendationAddInFlight = false;

class ShippingProgressBar extends HTMLElement {
  static get observedAttributes() {
    return ['threshold', 'country', 'currency', 'catalog'];
  }

  constructor() {
    super();
    this.thresholdAmount = null;
    this.destinationCountry = '';
    this.baseCurrency = '';
    this.catalogHref = '/collections/all';
    this.recommendations = [];
    this.selectedVariantIds = new Map();
    this.lastCart = null;
    this.renderRevision = 0;
    this.recommendationRevision = 0;
    this.contextRefreshQueued = false;
    this.cartController = null;
    this.lifecycleController = null;
  }

  connectedCallback() {
    this.lifecycleController?.abort();
    if (!this.hasAttribute('data-skip-subtree-update')) this.setAttribute('data-skip-subtree-update', '');
    this.thresholdAmount = this.readAmount(this.getAttribute('threshold'));
    this.destinationCountry = (this.getAttribute('country') || '').toUpperCase();
    this.baseCurrency = this.getAttribute('currency') || '';
    this.catalogHref = this.getAttribute('catalog') || '/collections/all';
    this.lifecycleController = new AbortController();
    this.renderShell();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate, {
      signal: this.lifecycleController.signal,
    });
    document.addEventListener(ThemeEvents.discountUpdate, this.onCartUpdate, {
      signal: this.lifecycleController.signal,
    });
    this.refreshCart();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.lifecycleController?.abort();
    this.cartController?.abort();
    this.renderRevision += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'threshold') this.thresholdAmount = this.readAmount(newValue);
    if (name === 'country') this.destinationCountry = (newValue || '').toUpperCase();
    if (name === 'currency') this.baseCurrency = newValue || '';
    if (name === 'catalog') this.catalogHref = newValue || '/collections/all';
    if (!this.isConnected || !this.lifecycleController) return;

    if (name === 'catalog') {
      this.loadRecommendations();
    } else if (name === 'country' || name === 'currency') {
      this.recommendationRevision += 1;
      const previousCart = this.lastCart;
      this.lastCart = null;
      const progress = this.querySelector('[data-progress]');
      if (this.destinationCountry !== 'US' || this.baseCurrency !== 'USD') {
        this.renderProgress({ ...(previousCart || {}), currency: this.baseCurrency });
        this.lastCart = null;
      } else if (progress) {
        progress.hidden = true;
      }
      const region = this.querySelector('[data-recommendation-region]');
      if (region) region.hidden = true;
      this.scheduleContextRefresh();
    } else if (this.lastCart) {
      this.renderProgress(this.lastCart);
    } else if (!this.cartController) {
      this.refreshCart();
    }
  }

  renderShell() {
    this.innerHTML = `
      <div class="shipping-progress" data-progress hidden>
        <div class="shipping-progress__bar-container">
          <div class="shipping-progress__bar" data-progress-bar style="width: 0%"></div>
        </div>
        <div class="shipping-progress__text" data-progress-message role="status" aria-live="polite"></div>
      </div>
      <div class="shipping-progress__recommendations" data-recommendation-region hidden>
        <span class="shipping-progress__rec-label">You may also like:</span>
        <div class="shipping-progress__rec-items" data-recommendations></div>
        <p class="shipping-progress__status" data-add-status role="status" aria-live="polite"></p>
      </div>
    `;
  }

  readAmount(value) {
    if (typeof value !== 'string' || value.trim() === '') return null;
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }

  getCurrency(cart) {
    const cartCurrency = typeof cart?.currency === 'string' ? cart.currency : '';
    const storefrontCurrency = window.Shopify?.currency?.active;
    return cartCurrency || this.baseCurrency || (typeof storefrontCurrency === 'string' ? storefrontCurrency : '');
  }

  getDisplayCurrency() {
    const storefrontCurrency = window.Shopify?.currency?.active;
    return this.baseCurrency || (typeof storefrontCurrency === 'string' ? storefrontCurrency : '');
  }

  scheduleContextRefresh() {
    if (this.contextRefreshQueued) return;
    this.contextRefreshQueued = true;
    queueMicrotask(() => {
      this.contextRefreshQueued = false;
      if (!this.isConnected || !this.lifecycleController) return;
      this.refreshCart();
      this.loadRecommendations();
    });
  }

  storefrontPath(path) {
    const root = window.Shopify?.routes?.root;
    const localeRoot = typeof root === 'string' && root.startsWith('/') ? root : '/';
    return `${localeRoot}${path.replace(/^\/+/, '')}`;
  }

  formatMoney(amount, currency = this.getDisplayCurrency()) {
    const value = Number(amount);
    if (!Number.isFinite(value) || !/^[A-Z]{3}$/.test(currency)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(value);
    } catch {
      return '';
    }
  }

  formatMinorMoney(amount, currency) {
    const value = Number(amount);
    if (!Number.isSafeInteger(value)) return '';
    // Shopify's Ajax and Liquid cart amounts use a 100x subunit scale even
    // for currencies such as JPY that don't display fractional units.
    return this.formatMoney(value / 100, currency);
  }

  async fetchCart(signal) {
    const response = await fetch(this.storefrontPath('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) throw new Error('The cart could not be refreshed.');
    const cart = await response.json();
    if (!cart || typeof cart.total_price !== 'number' || typeof cart.item_count !== 'number') {
      throw new Error('The cart response was incomplete.');
    }
    return cart;
  }

  async refreshCart(cart = null) {
    const revision = ++this.renderRevision;
    this.cartController?.abort();
    const controller = new AbortController();
    this.cartController = controller;

    try {
      const currentCart = cart || await this.fetchCart(controller.signal);
      if (revision !== this.renderRevision || !this.isConnected) return;
      this.renderProgress(currentCart);
    } catch {
      if (revision !== this.renderRevision || !this.isConnected || controller.signal.aborted) return;
      this.showProgressStatus('Shipping progress could not be refreshed.');
    }
  }

  onCartUpdate = (event) => {
    const detail = event.detail || {};
    const data = detail.data || detail;
    const sequence = detail.cartUpdateSequence ?? data.cartUpdateSequence;
    if (!isLatestCartUpdate(sequence)) return;
    if (data.cartReadFailed === true) {
      const outcome = data.addOutcome;
      const message = data.source === 'cart-discount'
        ? 'We could not confirm the cart total after the discount change. Check the cart before continuing.'
        : outcome === 'CONFIRMED'
        ? 'Your item was added, but shipping progress could not be refreshed. Open the cart to check your items.'
        : outcome === 'REJECTED'
          ? 'This item could not be added. Check your cart before trying again.'
          : 'We could not confirm whether the item was added. Check your cart before trying again.';
      this.showProgressStatus(message);
      this.setAddStatus(message);
      return;
    }
    const resource = detail.resource;
    if (resource && typeof resource.total_price === 'number' && typeof resource.item_count === 'number') {
      this.refreshCart(resource);
      return;
    }
    this.refreshCart();
  };

  showProgressStatus(message) {
    this.cartController?.abort();
    this.renderRevision += 1;
    this.lastCart = null;
    const progress = this.querySelector('[data-progress]');
    const status = this.querySelector('[data-progress-message]');
    if (!progress || !status || !this.thresholdAmount) return;
    progress.hidden = false;
    progress.classList.remove('shipping-progress--complete');
    const bar = this.querySelector('[data-progress-bar]');
    if (bar) bar.style.width = '0%';
    status.textContent = message;
  }

  renderProgress(cart) {
    this.lastCart = cart;
    const progress = this.querySelector('[data-progress]');
    const bar = this.querySelector('[data-progress-bar]');
    const message = this.querySelector('[data-progress-message]');
    if (!progress || !bar || !message || !this.thresholdAmount) return;

    const currency = this.getCurrency(cart);
    if (!currency) {
      progress.hidden = true;
      return;
    }

    if (this.destinationCountry !== 'US' || currency !== 'USD') {
      progress.hidden = false;
      progress.classList.remove('shipping-progress--complete');
      progress.classList.add('shipping-progress--informational');
      bar.style.width = '0%';
      message.textContent = 'Shipping is calculated at checkout.';
      return;
    }

    progress.classList.remove('shipping-progress--informational');
    const thresholdMinor = Math.round(this.thresholdAmount * 100);
    const remaining = thresholdMinor - cart.total_price;
    const complete = remaining <= 0;
    const percent = Math.max(0, Math.min(100, (cart.total_price / thresholdMinor) * 100));

    progress.hidden = false;
    progress.classList.toggle('shipping-progress--complete', complete);
    bar.style.width = `${percent}%`;
    message.textContent = complete
      ? "You've unlocked free shipping."
      : `Add ${this.formatMinorMoney(remaining, currency)} more for free shipping.`;
  }

  async loadRecommendations() {
    const revision = ++this.recommendationRevision;
    const region = this.querySelector('[data-recommendation-region]');
    this.recommendations = [];
    if (region) region.hidden = true;
    try {
      const response = await fetch(this.storefrontPath('collections/all/products.json?limit=10'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.lifecycleController?.signal,
      });
      if (!response.ok) throw new Error('Recommendations could not be loaded.');
      const data = await response.json();
      if (revision !== this.recommendationRevision || !this.isConnected) return;
      const products = (data.products || [])
        .filter((product) => {
          const title = (product.title || '').toLowerCase();
          return !title.includes('14k') && !title.includes('heirloom')
            && typeof product.handle === 'string' && product.handle.length > 0
            && Array.isArray(product.variants) && product.variants.length > 0;
        })
        .slice(0, 3);
      const loadedProducts = await Promise.all(products.map(async (product) => {
        try {
          const productResponse = await fetch(this.storefrontPath(`products/${encodeURIComponent(product.handle)}.js`), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: this.lifecycleController?.signal,
          });
          if (!productResponse.ok) return null;
          const currentProduct = await productResponse.json();
          if (currentProduct.handle !== product.handle || !Array.isArray(currentProduct.variants)) return null;
          const collectionVariantIds = new Set(product.variants.map((variant) => String(variant.id)));
          const variants = currentProduct.variants.filter((variant) => collectionVariantIds.has(String(variant.id)));
          if (variants.length === 0) return null;
          return { ...product, variants };
        } catch {
          return null;
        }
      }));
      if (revision !== this.recommendationRevision || !this.isConnected) return;
      this.recommendations = loadedProducts.filter(Boolean);
      this.updateRecommendations();
      if (region) region.hidden = this.recommendations.length === 0;
    } catch {
      if (revision === this.recommendationRevision && region) region.hidden = true;
    }
  }

  updateRecommendations() {
    const container = this.querySelector('[data-recommendations]');
    const region = this.querySelector('[data-recommendation-region]');
    if (!container || !region) return;

    container.replaceChildren();
    for (const product of this.recommendations) {
      if (!product.handle || !Array.isArray(product.variants) || product.variants.length === 0) continue;

      const row = document.createElement('div');
      row.className = 'shipping-progress__rec-item';
      const productUrl = this.storefrontPath(`products/${encodeURIComponent(product.handle)}`);

      if (product.images?.[0]?.src) {
        const imageLink = document.createElement('a');
        imageLink.className = 'shipping-progress__rec-image-link';
        imageLink.href = productUrl;
        imageLink.setAttribute('aria-label', `View ${product.title || 'product'}`);
        const image = document.createElement('img');
        image.src = product.images[0].src;
        image.alt = product.title || '';
        image.className = 'shipping-progress__rec-image';
        image.loading = 'lazy';
        imageLink.appendChild(image);
        row.appendChild(imageLink);
      }

      const info = document.createElement('div');
      info.className = 'shipping-progress__rec-info';
      const title = document.createElement('a');
      title.className = 'shipping-progress__rec-title';
      title.href = productUrl;
      title.textContent = product.title || '';
      const price = document.createElement('span');
      price.className = 'shipping-progress__rec-price';
      info.append(title, price);

      const requiresChoice = product.variants.length > 1
        || (Array.isArray(product.options) && product.options.some((option) => Array.isArray(option.values) && option.values.length > 1));
      const select = requiresChoice ? document.createElement('select') : null;
      if (select) select.disabled = recommendationAddInFlight;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shipping-progress__rec-add button button--small';
      button.dataset.shippingProgressAdd = 'true';
      button.dataset.recommendationRevision = String(this.recommendationRevision);
      button.dataset.canAdd = 'false';
      button.disabled = true;
      button.textContent = 'Add';

      if (select) {
        select.className = 'shipping-progress__rec-variant';
        select.setAttribute('aria-label', `Choose an option for ${product.title || 'this product'}`);
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Choose an option';
        placeholder.selected = true;
        placeholder.disabled = true;
        select.appendChild(placeholder);

        for (const variant of product.variants) {
          const option = document.createElement('option');
          option.value = String(variant.id);
          const variantPrice = this.formatMinorMoney(variant.price, this.getCurrency(this.lastCart || {}));
          option.textContent = [variant.title, variantPrice].filter(Boolean).join(' — ');
          option.disabled = variant.available !== true || !variantPrice;
          select.appendChild(option);
        }

        const applyVariant = (variant) => {
          if (variant) this.selectedVariantIds.set(product.handle, String(variant.id));
          else this.selectedVariantIds.delete(product.handle);
          button.dataset.variantId = variant ? String(variant.id) : '';
          const formattedPrice = variant ? this.formatMinorMoney(variant.price, this.getCurrency(this.lastCart || {})) : '';
          button.dataset.canAdd = variant?.available === true && formattedPrice ? 'true' : 'false';
          button.disabled = button.dataset.canAdd !== 'true' || recommendationAddInFlight;
          price.textContent = variant ? formattedPrice || 'Price unavailable' : '';
        };

        const selectedVariantId = this.selectedVariantIds.get(product.handle);
        const selectedVariant = product.variants.find((variant) => String(variant.id) === selectedVariantId
          && variant.available === true && this.formatMinorMoney(variant.price, this.getCurrency(this.lastCart || {})));
        if (selectedVariant) {
          select.value = String(selectedVariant.id);
          applyVariant(selectedVariant);
        }

        select.addEventListener('change', () => {
          const variant = product.variants.find((item) => String(item.id) === select.value);
          applyVariant(variant);
        });
      } else {
        const variant = product.variants[0];
        const formattedPrice = this.formatMinorMoney(variant.price, this.getCurrency(this.lastCart || {}));
        button.dataset.variantId = String(variant.id);
        button.dataset.canAdd = variant.available === true && formattedPrice ? 'true' : 'false';
        button.disabled = button.dataset.canAdd !== 'true' || recommendationAddInFlight;
        price.textContent = formattedPrice || 'Price unavailable';
      }

      button.addEventListener('click', () => this.addRecommended(button));
      if (select) info.appendChild(select);
      row.append(info, button);
      container.appendChild(row);
    }
    region.hidden = container.childElementCount === 0;
  }

  async addRecommended(button) {
    const recommendationRevision = this.recommendationRevision;
    if (button.dataset.recommendationRevision
      && Number(button.dataset.recommendationRevision) !== recommendationRevision) return;
    const variantId = button.dataset.variantId;
    if (!variantId || button.dataset.canAdd !== 'true' || recommendationAddInFlight) return;

    recommendationAddInFlight = true;
    this.setAddControlsDisabled(true);
    this.setAddStatus('Adding to your cart…');

    try {
      await runCartMutation(async (cartUpdateSequence) => {
        if (recommendationRevision !== this.recommendationRevision) {
          this.setAddStatus('Recommendations changed. Please choose the current option before adding.');
          return;
        }
        let addStarted = false;
        let addConfirmed = false;
        let addRejected = false;
        let responseSections;

        try {
          const sectionIds = [...new Set(
            Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
              .map((element) => element.dataset.sectionId)
              .filter(Boolean),
          )];
          const body = { id: variantId, quantity: 1 };
          if (sectionIds.length > 0) {
            body.sections = sectionIds.join(',');
            body.sections_url = window.location.pathname;
          }

          addStarted = true;
          const response = await fetch(this.storefrontPath('cart/add.js'), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          responseSections = result.sections;
          if (!response.ok || result.status) {
            const statusCode = Number(result.status) || response.status;
            // Shopify may partially add the requested quantity and return 422.
            addRejected = statusCode >= 400 && statusCode < 500 && statusCode !== 422;
            throw new Error(result.description || result.message || 'This item could not be added.');
          }
          addConfirmed = true;

          let cart;
          try {
            cart = await this.fetchCart();
          } catch {
            cart = null;
          }

          const itemCount = cart?.item_count ?? this.readItemCountFromSections(result.sections);
          const sections = result.sections && typeof result.sections === 'object' ? result.sections : undefined;
          document.dispatchEvent(new CartAddEvent(cart || {}, String(variantId), {
            source: 'shipping-progress',
            ...(itemCount !== null ? { itemCount } : {}),
            variantId: String(variantId),
            sections,
            cartReadFailed: !cart,
            addOutcome: 'CONFIRMED',
            cartUpdateSequence,
          }));

          if (cart) {
            this.setAddStatus('Added to your cart.');
          } else {
            this.setAddStatus('Added, but the cart could not be refreshed. Open the cart to check your items.');
          }
        } catch (error) {
          let confirmedRejected = false;
          if (addStarted && !addConfirmed) {
            let cart;
            try {
              cart = await this.fetchCart();
            } catch {
              cart = null;
            }
            confirmedRejected = Boolean(cart && addRejected);
            if (isLatestCartUpdate(cartUpdateSequence)) {
              document.dispatchEvent(new CartAddEvent(cart || {}, String(variantId), {
                source: 'shipping-progress',
                didError: true,
                ...(cart ? { itemCount: cart.item_count } : {}),
                variantId: String(variantId),
                sections: responseSections,
                cartReadFailed: !cart,
                addOutcome: confirmedRejected ? 'REJECTED' : 'UNKNOWN',
                cartUpdateSequence,
              }));
            }
          }
          const message = confirmedRejected && error instanceof Error
            ? error.message
            : addStarted && !addConfirmed
              ? 'We could not confirm whether the item was added. Check your cart before trying again.'
              : error instanceof Error
                ? error.message
                : 'This item could not be added. Please try again.';
          this.setAddStatus(message);
        }
      });
    } finally {
      recommendationAddInFlight = false;
      this.setAddControlsDisabled(false);
    }
  }

  readItemCountFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      const section = new DOMParser().parseFromString(html, 'text/html');
      const count = Number(section.querySelector('[ref="cartItemCount"]')?.textContent?.trim());
      if (Number.isInteger(count) && count >= 0) return count;
    }
    return null;
  }

  setAddControlsDisabled(disabled) {
    document.querySelectorAll('[data-shipping-progress-add="true"]').forEach((button) => {
      const canAdd = button.dataset.canAdd === 'true';
      button.disabled = disabled || !canAdd;
    });
    document.querySelectorAll('select.shipping-progress__rec-variant').forEach((select) => {
      select.disabled = disabled;
    });
  }

  setAddStatus(message) {
    const status = this.querySelector('[data-add-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('shipping-progress-bar')) {
  customElements.define('shipping-progress-bar', ShippingProgressBar);
}
